// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from "react";
import SharedSelect from "../components/SharedSelect";
import CardAutocompleteInput from "../components/CardAutocompleteInput";
import { useNavigate, useParams } from "react-router-dom";
import { getUser } from "../auth/auth";
import { fetchPublicUserById } from "../api/users";
import { getBountyById } from "../api/bounties";
import { createTradeOfferMultipart } from "../api/tradeOffers";
import { getTradeById } from "../api/trades";
import { useToast } from "../components/ToastProvider";
import {
  IMAGE_INPUT_ACCEPT_ATTR,
  MAX_IMAGE_BYTES_DEFAULT,
  validateImageFile,
} from "../utils/imageUploadValidation";
import "../styles/CardAutocomplete.css";
import "../styles/TradeOffer.css";

function TradeOffer() {
  const navigate = useNavigate();
  const { id, type } = useParams(); // id is trade/bounty ID, type is 'trade' or 'bounty'
  const { showToast } = useToast();
  const user = getUser();
  const userId = user?.id ?? null;

  const [trade, setTrade] = useState(null);
  const [bounty, setBounty] = useState(null);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const idempotencyKeyRef = useRef(null);

  const [offerData, setOfferData] = useState({
    offerCardName: "",
    offerGame: "",
    offerCardCondition: "Near Mint",
    offerGradingCompany: "raw",
    offerGradeNumber: "",
    offerPhotoUrl: "",
    message: ""
  });
  const [offerPhotoFile, setOfferPhotoFile] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);

  const generateIdempotencyKey = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const isTimeoutOrNetworkError = (error) => {
    const status = Number(error?.status || 0);
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();
    return (
      status === 0 ||
      code.includes("timeout") ||
      code.includes("network") ||
      message.includes("timeout") ||
      message.includes("network")
    );
  };

  const isOwnListingOwner = useCallback(
    (ownerId) =>
      String(ownerId || "").toLowerCase() === String(userId || "").toLowerCase(),
    [userId]
  );

  const fetchData = useCallback(async () => {
    try {
      // Fetch based on type (from URL like /trade-offer/TRADE-123 or /bounty-offer/BOUNTY-456)
      const isBounty = window.location.pathname.includes("bounty-offer");
      
      if (isBounty) {
        const found = id ? await getBountyById(id) : null;
        if (found) {
          if (isOwnListingOwner(found.userId)) {
            showToast("You cannot fulfill your own bounty.", "warning");
            navigate("/profile", { replace: true });
            return;
          }
          const ownerData = await fetchPublicUserById(found.userId);
          setBounty(found);
          setOwner(ownerData);
          setOfferData(prev => ({
            ...prev,
            offerCardName: found.cardName || "",
            offerGame: found.cardGame || found.game || ""
          }));
          if (found.cardId && (found.cardGame || found.game)) {
            setSelectedCard({
              game: found.cardGame || found.game,
              cardId: found.cardId,
              cardName: found.cardNameCanonical || found.cardName || "",
            });
          }
        } else {
          setBounty(found);
        }
      } else {
        const found = id ? await getTradeById(id) : null;
        setTrade(found);
        if (found) {
          const ownerData = await fetchPublicUserById(found.userId);
          setOwner(ownerData);
          setOfferData(prev => ({
            ...prev,
            offerGame: found.game || ""
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [id, isOwnListingOwner, navigate, showToast]);

  useEffect(() => {
    if (!userId) {
      navigate("/login");
      return;
    }

    fetchData();
  }, [userId, navigate, type, fetchData]);

  useEffect(() => {
    return () => {
      if (offerData.offerPhotoUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(offerData.offerPhotoUrl);
      }
    };
  }, [offerData.offerPhotoUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    idempotencyKeyRef.current = null;
    setOfferData(prev => {
      const next = {
        ...prev,
        [name]: value
      };

      if (name === "offerGame") {
        setSelectedCard(null);
      }

      if (name === "offerGradingCompany") {
        if (value === "raw") {
          next.offerGradeNumber = "";
        }
      }

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!bounty && !offerData.offerCardName) {
      showToast("Please enter the card you're offering", "warning");
      return;
    }
    if (!bounty && !offerData.offerGame) {
      showToast("Please choose a game for the card you're offering.", "warning");
      return;
    }
    if (!bounty && String(offerData.offerGame || "").trim().toLowerCase() !== "one piece" && !selectedCard) {
      showToast("Please select a valid card from suggestions.", "warning");
      return;
    }

    if (bounty && !offerPhotoFile) {
      showToast("Please upload a photo of the card you're using to fulfill the bounty.", "warning");
      return;
    }

    if (bounty && isOwnListingOwner(owner?.id || bounty?.userId)) {
      showToast("You cannot fulfill your own bounty.", "warning");
      navigate("/profile");
      return;
    }

    try {
      setIsSubmitting(true);
      const idempotencyKey = idempotencyKeyRef.current || generateIdempotencyKey();
      idempotencyKeyRef.current = idempotencyKey;
      const offerCardName = bounty?.cardName || offerData.offerCardName;
      const canonicalSelection = bounty
        ? (bounty?.cardId && (bounty?.cardGame || bounty?.game)
            ? {
                game: bounty.cardGame || bounty.game,
                cardId: bounty.cardId,
                cardName: bounty.cardNameCanonical || bounty.cardName || "",
              }
            : null)
        : selectedCard;

      const result = await createTradeOfferMultipart({
        fromUserId: user.id,
        toUserId: owner.id,
        tradeId: trade?.id || null,
        bountyId: bounty?.id || null,
        offerCardName,
        offerCardGame: canonicalSelection?.game || null,
        offerCardId: canonicalSelection?.cardId || null,
        offerCardNameCanonical: canonicalSelection?.cardName || null,
        offerCardCondition: offerData.offerCardCondition,
        offerGradingCompany: offerData.offerGradingCompany,
        offerGradeNumber: offerData.offerGradeNumber,
        offerCardValue: 0,
        cashAmount: 0,
        message: offerData.message,
        file: offerPhotoFile,
        idempotencyKey,
      });

      showToast("Offer sent! The owner will be notified.", "success");
      idempotencyKeyRef.current = null;
      navigate(result?.offer?.id ? `/offer/${result.offer.id}` : "/local-trades");
    } catch (error) {
      console.error("Error sending offer:", error);
      if (String(error?.code || "") === "idempotency_request_in_progress") {
        showToast("Your offer is still processing. Please wait a moment before trying again.", "warning");
        return;
      }
      if (isTimeoutOrNetworkError(error)) {
        showToast(
          "The request timed out. Your offer may still be processing. Please check sent offers before retrying.",
          "warning"
        );
        return;
      }
      showToast("Failed to send offer. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="trade-offer-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!trade && !bounty) {
    return (
      <div className="trade-offer-page">
        <div className="error-message">Listing not found</div>
      </div>
    );
  }

  const listing = trade || bounty;
  const isBounty = !!bounty;
  const isOnePieceOfferGame = String(offerData.offerGame || "").trim().toLowerCase() === "one piece";

  const bountyWarning = (() => {
    if (!isBounty || !bounty) return "";

    const bountyCondition = String(bounty.desiredCondition || "").toLowerCase();
    const offerCondition = String(offerData.offerCardCondition || "").toLowerCase();
    const conditionDiffers = bountyCondition && bountyCondition !== offerCondition;

    const bountyGradingCompany = String(bounty.gradingCompany || "").toLowerCase();
    const offerGradingCompany = String(offerData.offerGradingCompany || "").toLowerCase();
    const gradingDiffers = bountyGradingCompany && bountyGradingCompany !== offerGradingCompany;

    const bountyGrade = bounty.gradeNumber !== undefined && bounty.gradeNumber !== null
      ? String(bounty.gradeNumber)
      : "";
    const offerGrade = String(offerData.offerGradeNumber || "");
    const gradeDiffers = bountyGrade && bountyGrade !== offerGrade;

    if (conditionDiffers || gradingDiffers || gradeDiffers) {
      return "This differs from the bounty’s preferred condition or grading. The owner will review.";
    }

    return "";
  })();

  const handlePhotoChange = (e) => {
    idempotencyKeyRef.current = null;
    const file = e.target.files && e.target.files[0];

    if (!file) {
      setOfferPhotoFile(null);
      if (offerData.offerPhotoUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(offerData.offerPhotoUrl);
      }
      setOfferData(prev => ({
        ...prev,
        offerPhotoUrl: ""
      }));
      return;
    }

    const validation = validateImageFile(file, { maxBytes: MAX_IMAGE_BYTES_DEFAULT });
    if (!validation.ok) {
      showToast(validation.error || "Please upload a valid image.", "error");
      e.target.value = "";
      return;
    }

    if (offerData.offerPhotoUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(offerData.offerPhotoUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    setOfferPhotoFile(file);
    setOfferData(prev => ({
      ...prev,
      offerPhotoUrl: previewUrl
    }));
  };

  return (
    <div className="trade-offer-page">
      <div className="trade-offer-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ←
        </button>
        <div>
          <h1>Propose a Trade</h1>
          <p>Propose an in-person trade with the card you'd exchange.</p>
        </div>
      </div>

      <div className="offer-container">
        {/* What They Have/Want */}
        <div className="listing-info">
          <h2>{isBounty ? "What They're Looking For" : "What They're Offering"}</h2>
          
          <div className="info-card">
            {listing.photoUrl && (
              <div className="info-photo">
                <img src={listing.photoUrl} alt={listing.cardName} />
              </div>
            )}
            <div className="info-header">
              <h3>{listing.cardName}</h3>
              <span className="game-badge">{listing.game}</span>
            </div>

            <div className="info-details">
              <div className="detail-row">
                <span className="label">Owner:</span>
                <span className="value">
                  {owner?.username} 
                  {owner?.verified && " (Verified)"}
                  {owner?.trustScore && ` (Trust: ${owner.trustScore})`}
                </span>
              </div>

              {!isBounty && (
                <div className="detail-row">
                  <span className="label">Condition:</span>
                  <span className="value">{listing.condition}</span>
                </div>
              )}

              {isBounty && (!listing.gradingCompany || listing.gradingCompany === "raw") && (
                <div className="detail-row">
                  <span className="label">Minimum Condition:</span>
                  <span className="value">{listing.desiredCondition}</span>
                </div>
              )}

              {isBounty && listing.gradingCompany && listing.gradingCompany !== "raw" && (
                <div className="detail-row">
                  <span className="label">Grading Company:</span>
                  <span className="value">{listing.gradingCompany.toUpperCase()}</span>
                </div>
              )}

              {isBounty && listing.gradeNumber && (
                <div className="detail-row">
                  <span className="label">Grade Number:</span>
                  <span className="value">{listing.gradeNumber}</span>
                </div>
              )}


              <div className="detail-row">
                <span className="label">Location:</span>
                <span className="value">ZIP {listing.zipCode} ({listing.radius} mi)</span>
              </div>

              {!isBounty && listing.lookingFor && listing.lookingFor.length > 0 && (
                <div className="detail-row">
                  <span className="label">Looking for:</span>
                  <div className="looking-for-tags">
                    {listing.lookingFor.map((item, idx) => (
                      <span key={idx} className="tag">{item}</span>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Your Offer */}
        <div className="offer-form-section">
          <h2>{isBounty ? "Bounty Fulfillment Details" : "Your Offer"}</h2>
          {isBounty && (
            <p className="form-helper">This bounty can only be fulfilled by providing the requested card.</p>
          )}
          
          <form className="offer-form" onSubmit={handleSubmit}>
            {!isBounty && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Game *</label>
                    <SharedSelect
                      name="offerGame"
                      value={offerData.offerGame}
                      onChange={handleChange}
                    >
                      <option value="">Select a game</option>
                      <option>Magic: The Gathering</option>
                      <option>Pokémon</option>
                      <option>Yu-Gi-Oh!</option>
                      <option>One Piece</option>
                    </SharedSelect>
                  </div>
                  <div className="form-group match-guidelines">
                    <label>Card You're Offering *</label>
                    <div className="input-with-feedback trade-offer-autocomplete">
                      <CardAutocompleteInput
                        id="tradeOfferCardName"
                        game={offerData.offerGame}
                        value={offerData.offerCardName}
                        onValueChange={(nextValue) =>
                          setOfferData((prev) => ({ ...prev, offerCardName: nextValue }))
                        }
                        selectedCard={selectedCard}
                        onSelectCard={setSelectedCard}
                        placeholder="Search and select card"
                        required
                      />
                      {selectedCard && <span className="check-icon">✓</span>}
                    </div>
                    {isOnePieceOfferGame && (
                      <p className="dropzone-helper trade-offer-helper-left">
                        One Piece canonical validation coming soon.
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            {isBounty && (
              <div className="form-row">
                <div className="form-group">
                  <label>Game</label>
                  <input type="text" value={offerData.offerGame || bounty?.game || ""} disabled />
                </div>
                <div className="form-group match-guidelines">
                  <label>Card You Are Fulfilling With</label>
                  <input type="text" value={offerData.offerCardName} disabled />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{isBounty ? "Card Photos (Required)" : "Card Photos (Strongly encouraged)"}</label>
              <div className="photo-dropzone">
                {!offerData.offerPhotoUrl ? (
                  <label className="dropzone-label">
                    <input
                      type="file"
                      accept={IMAGE_INPUT_ACCEPT_ATTR}
                      onChange={handlePhotoChange}
                      style={{ display: "none" }}
                    />
                    <div className="dropzone-content">
                      <span className="upload-text">Upload clear photos of the card</span>
                    </div>
                    <p className="dropzone-helper">
                      {isBounty
                        ? "Required to verify the card you are fulfilling with"
                        : "Helps the owner verify condition before meeting"}
                    </p>
                  </label>
                ) : (
                  <div className="photo-preview-box">
                    <img src={offerData.offerPhotoUrl} alt="Offer card preview" />
                    <label className="replace-photo-link">
                      <input
                        type="file"
                        accept={IMAGE_INPUT_ACCEPT_ATTR}
                        onChange={handlePhotoChange}
                        style={{ display: "none" }}
                      />
                      Replace photo
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Condition *</label>
                <SharedSelect
                  name="offerCardCondition"
                  value={offerData.offerCardCondition}
                  onChange={handleChange}
                  disabled={!isBounty && offerData.offerGradingCompany !== "raw"}
                >
                  <option>Near Mint</option>
                  <option>Lightly Played</option>
                  <option>Moderately Played</option>
                  <option>Heavily Played</option>
                  <option>Damaged</option>
                </SharedSelect>
              </div>
              <div className="form-group match-guidelines">
                <label>Grading Company</label>
                <SharedSelect
                  name="offerGradingCompany"
                  value={offerData.offerGradingCompany}
                  onChange={handleChange}
                >
                  <option value="raw">Raw (Ungraded)</option>
                  <option value="psa">PSA</option>
                  <option value="bgs">BGS</option>
                  <option value="cgc">CGC</option>
                  <option value="tag">TAG</option>
                  <option value="sgc">SGC</option>
                  <option value="other">Other</option>
                </SharedSelect>
              </div>
            </div>

            {offerData.offerGradingCompany !== "raw" && (
              <div className="form-group match-guidelines">
                <label>Grade Number</label>
                <SharedSelect
                  name="offerGradeNumber"
                  value={offerData.offerGradeNumber}
                  onChange={handleChange}
                >
                  <option value="">Any</option>
                  <option value="10">10</option>
                  <option value="9.5">9.5</option>
                  <option value="9">9</option>
                  <option value="8.5">8.5</option>
                  <option value="8">8</option>
                  <option value="7.5">7.5</option>
                  <option value="7">7</option>
                  <option value="6">6</option>
                  <option value="5">5</option>
                  <option value="4">4</option>
                  <option value="3">3</option>
                  <option value="2">2</option>
                  <option value="1">1</option>
                </SharedSelect>
              </div>
            )}

            {bountyWarning && (
              <div className="form-warning">
                {bountyWarning}
              </div>
            )}

            <div className="form-group match-guidelines">
              <label>Message to Owner</label>
              <textarea
                name="message"
                value={offerData.message}
                onChange={handleChange}
                placeholder={
                  isBounty
                    ? "Introduce yourself and suggest a meetup time/location..."
                    : "Introduce yourself, explain why you want to trade, suggest meetup locations..."
                }
                rows="4"
              />
            </div>

            <div className="safety-notice">
              <h3>In-Person Trade Guidelines</h3>
              <ul>
                <li>All Cardex trades are completed in person</li>
                <li>Meet in a public, well-lit location</li>
                <li>Inspect cards together before confirming the trade</li>
                <li>Review the trader's trust score and completed trades</li>
                <li>Trades can be canceled if something feels off</li>
              </ul>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : isBounty ? "Send Fulfillment" : "Send Trade Proposal"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default TradeOffer;
